import type { CameraTarget } from '@mighty-map/map-engine';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  canGoBack,
  canGoForward,
  EMPTY_HISTORY,
  recordView,
  stepView,
  type CameraHistory,
} from '../../lib/cameraHistory';
import type { ViewCamera } from '../../lib/viewCamera';
import { homeCameraFor } from '../globe/viewSync';
import { GLYPHS, LineIcon } from './icons';

const LOCATE_ZOOM = 15;

function Tool({
  title,
  onClick,
  disabled = false,
  busy = false,
  children,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="mm-maptool"
      title={title}
      aria-label={title}
      aria-disabled={disabled || undefined}
      data-busy={busy || undefined}
      onClick={() => {
        if (!disabled) onClick();
      }}
    >
      {children}
    </button>
  );
}

/**
 * The left map-tools column: zoom, home (the theme's camera), previous / next extent, find my location and the
 * compass. Talks to the view on screen only through its ViewCamera: the map's or the globe's engine, or in BIM the
 * model camera. Previous and next keep one history for Map and Globe and one for BIM (`history` names it), so a step
 * back in BIM goes to the last place on the model, not to a flat map view.
 */
export function MapControls({
  camera,
  history: historyKey,
  home,
  homeView = 'map',
  globeHome,
  minimised,
  onToggleMinimised,
}: {
  camera: ViewCamera | null;
  history: string;
  home: CameraTarget | null;
  /** Which view Home applies in (map vs globe). */
  homeView?: 'map' | 'globe';
  globeHome?: { bearing?: number; pitch?: number } | undefined;
  minimised: boolean;
  onToggleMinimised: () => void;
}) {
  const [histories, setHistories] = useState<Record<string, CameraHistory>>({});
  const history = histories[historyKey] ?? EMPTY_HISTORY;
  const historyRef = useRef(history);
  historyRef.current = history;
  const setHistory = useCallback(
    (next: (h: CameraHistory) => CameraHistory) =>
      setHistories((all) => ({ ...all, [historyKey]: next(all[historyKey] ?? EMPTY_HISTORY) })),
    [historyKey],
  );
  const [bearing, setBearing] = useState(0);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);

  useEffect(() => {
    if (!camera) return;
    const settle = (): void => {
      const cam = camera.getCamera();
      if (!cam) return;
      setBearing(cam.bearing);
      setHistory((h) => recordView(h, cam));
    };
    settle();
    return camera.onCameraChange(settle);
  }, [camera, setHistory]);

  const zoomBy = (delta: number): void => {
    const cam = camera?.getCamera();
    if (!camera || !cam) return;
    camera.jumpTo({ zoom: cam.zoom + delta });
  };
  const step = (direction: -1 | 1): void => {
    const next = stepView(historyRef.current, direction);
    if (!camera || !next) return;
    setHistory(() => next.history);
    camera.jumpTo({ ...next.view });
  };
  const locate = (): void => {
    if (!camera) return;
    if (!('geolocation' in navigator)) {
      setLocateError('This browser cannot share its location.');
      return;
    }
    setLocating(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const center = [pos.coords.longitude, pos.coords.latitude] as const;
        camera.markLocation(center[0], center[1]);
        camera.jumpTo({ center, zoom: Math.max(camera.getCamera()?.zoom ?? 0, LOCATE_ZOOM) });
      },
      (err) => {
        setLocating(false);
        setLocateError(
          err.code === err.PERMISSION_DENIED ? 'Location permission was refused.' : 'Location unavailable.',
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );
  };

  const ready = camera !== null;
  return (
    <>
      <div className="mm-maptools" aria-label="Map tools" role="toolbar" aria-orientation="vertical">
        <Tool title="Zoom in" onClick={() => zoomBy(1)} disabled={!ready}>
          <LineIcon>{GLYPHS.zoomIn}</LineIcon>
        </Tool>
        <Tool title="Zoom out" onClick={() => zoomBy(-1)} disabled={!ready}>
          <LineIcon>{GLYPHS.zoomOut}</LineIcon>
        </Tool>
        <Tool
          title="Default map view"
          onClick={() => home && camera?.goHome(homeCameraFor(home, homeView, globeHome))}
          disabled={!ready || !home}
        >
          <LineIcon>{GLYPHS.home}</LineIcon>
        </Tool>
        <Tool title="Previous extent" onClick={() => step(-1)} disabled={!ready || !canGoBack(history)}>
          <LineIcon>{GLYPHS.previous}</LineIcon>
        </Tool>
        <Tool title="Next extent" onClick={() => step(1)} disabled={!ready || !canGoForward(history)}>
          <LineIcon>{GLYPHS.next}</LineIcon>
        </Tool>
        <Tool title={locateError ?? 'Find my location'} onClick={locate} disabled={!ready} busy={locating}>
          <LineIcon>{GLYPHS.locate}</LineIcon>
        </Tool>
        <Tool title="Reset map orientation" onClick={() => camera?.jumpTo({ bearing: 0 })} disabled={!ready}>
          <span style={{ display: 'grid', transform: `rotate(${-bearing}deg)` }}>
            <LineIcon>{GLYPHS.compass}</LineIcon>
          </span>
        </Tool>
      </div>
      <button
        type="button"
        className="mm-tools-tab"
        title={minimised ? 'Show map tools' : 'Hide map tools'}
        aria-label={minimised ? 'Show map tools' : 'Hide map tools'}
        aria-expanded={!minimised}
        onClick={onToggleMinimised}
      >
        <LineIcon stroke={2.2}>{minimised ? GLYPHS.chevronRight : GLYPHS.chevronLeft}</LineIcon>
      </button>
    </>
  );
}
